import { upperFirst } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';

type LocalizedCalendarDayOptions = {
	/** Whether to include the time in the formatted date. */
	includeTime?: boolean;
	/** Whether to capitalize the formatted date. */
	capitalize?: boolean;
};

/**
 * Formats a date as a localized calendar day.
 * See {@link momentDayCalendarFormat} for the format used.
 * @param date The date to format.
 * @param options The options for the formatted date.
 * @returns The formatted date.
 */
export function useLocalizedCalendarDay(
	date: moment.MomentInput | undefined,
	options: LocalizedCalendarDayOptions = {},
): string | undefined {
	const formatted = useMemo(
		() =>
			date
				? moment(date).calendar(
						undefined,
						options.includeTime ? momentDayWithTimeCalendarFormat : momentDayCalendarFormat,
					)
				: undefined,
		[date, options.includeTime],
	);

	return options.capitalize && formatted ? upperFirst(formatted) : formatted;
}

/**
 * A locale aware calendar format without time (unlike the format used by default by `momentInstance.calendar`, which includes the time).
 *
 * This supports all the Treams locales.
 * @see https://momentjs.com/docs/#/displaying/calendar-time/
 */
const momentDayCalendarFormat: moment.CalendarSpec = {
	...Object.fromEntries(
		['sameDay', 'nextDay', 'lastDay', 'lastWeek'].map((key) => [
			key,
			function (input: moment.Moment) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const originalFormat = (input as any)._locale._calendar[key];
				const withTime: string =
					typeof originalFormat === 'function' ? originalFormat.call(this) : originalFormat;

				const withoutTime = getFormatWithoutTime(withTime);
				return splitAtComponent(withoutTime, input.locale()).format;
			},
		]),
	),
	nextWeek: 'dddd',
	sameElse: 'LL',
};

const momentDayWithTimeCalendarFormat: moment.CalendarSpec = {
	sameElse(input: moment.Moment) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const sameDayFormat = (input as any)._locale._calendar.lastWeek;
		const withTime: string =
			typeof sameDayFormat === 'function' ? sameDayFormat.call(this) : sameDayFormat;

		const withoutTime = getFormatWithoutTime(withTime);
		const atComponent = splitAtComponent(withoutTime, input.locale()).atComponent;

		return ['LL', atComponent, 'LT'].join(' ');
	},
};

/**
 * Remove time from a built-in calendar format.
 * @param format The original format string.
 * @returns The format string without the time.
 */
function getFormatWithoutTime(format: string) {
	const withoutTimeUntrimmed = format.replace('LT', '').replace('  ', '').trim();
	return withoutTimeUntrimmed.endsWith(' ]')
		? withoutTimeUntrimmed.slice(0, -2).concat(']')
		: withoutTimeUntrimmed;
}

/**
 * Splits off the 'at' part of a format (e.g. 'Today at 10:00') where the time was previously removed (using {@link getFormatWithoutTime})
 * @param originalFormat The original format string.
 * @param locale The locale to use.
 * @returns An object containing the format string and the 'at' component.
 */
function splitAtComponent(originalFormat: string, locale: string) {
	const components = originalFormat.split(' ');
	const atSize =
		{
			es: 2,
			sv: 0,
		}[locale] ?? 1;
	let atComponent = components.splice(-atSize, atSize).join(' ');
	const needsClosingBracket =
		atComponent && atComponent.endsWith(']') && !atComponent.startsWith('[');

	if (needsClosingBracket) {
		atComponent = '[' + atComponent;
	}

	const format = components.join(' ').concat(needsClosingBracket ? ']' : '');

	return {
		format,
		atComponent,
	};
}
